/**
 * FotoKtpSheet Component
 *
 * Side panel for reject/approve foto actions.
 */

import * as React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import type { FotoKtpMember } from "./FotoKtpGrid";
import { KtpPhotoCell } from "./KtpPhotoCell";
import { ImageZoomModal } from "@/components/common/ImageZoomModal";

interface FotoKtpSheetProps {
  member: FotoKtpMember | null;
  mode: "reject" | "approve";
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (member: FotoKtpMember, mode: "reject" | "approve", reason?: string) => Promise<void>;
  loading: boolean;
}

export function FotoKtpSheet({ member, mode, open, onOpenChange, onConfirm, loading }: FotoKtpSheetProps) {
  const [reason, setReason] = React.useState("");
  const [zoomOpen, setZoomOpen] = React.useState(false);
  const [zoomSrc, setZoomSrc] = React.useState<string | null>(null);
  const [zoomTitle, setZoomTitle] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setReason("");
    }
  }, [open]);

  if (!member) return null;

  const handleConfirm = async () => {
    if (mode === "reject" && !reason.trim()) return;
    await onConfirm(member, mode, mode === "reject" ? reason.trim() : undefined);
  };

  const handleZoom = (src: string, title: string) => {
    setZoomSrc(src);
    setZoomTitle(title);
    setZoomOpen(true);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[500px] sm:w-[600px] sm:max-w-none overflow-y-auto">
          <SheetHeader className="px-6 pt-6">
            <SheetTitle>
              {mode === "reject" ? "Tandai Tidak Memenuhi Kriteria" : "Lepas Foto Hold"}
            </SheetTitle>
            <SheetDescription>
              {mode === "reject"
                ? `Masukkan alasan penolakan untuk ${member.callsign}. Pengguna akan diminta unggah ulang.`
                : `Apakah Anda yakin ingin melepas hold untuk foto ${member.callsign}?`}
            </SheetDescription>
          </SheetHeader>

          <div className="px-6 py-4 space-y-4">
            {/* Member Info */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              {member.foto ? (
                <img
                  src={member.foto}
                  alt={member.nama_lengkap}
                  className="h-14 w-10 object-cover rounded border cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => handleZoom(member.foto!, `Foto Profil - ${member.nama_lengkap}`)}
                />
              ) : (
                <div className="h-14 w-10 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
                  -
                </div>
              )}
              <div>
                <p className="font-semibold text-sm">{member.nama_lengkap}</p>
                <p className="text-xs font-mono text-muted-foreground">{member.callsign}</p>
                {member.isFotoValid === false && (
                  <Badge variant="destructive" className="text-[10px] mt-1">Ditolak</Badge>
                )}
              </div>
            </div>

            {/* KTP Photo Preview */}
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Foto KTP</p>
              <div className="h-48 rounded-lg overflow-hidden bg-muted">
                <KtpPhotoCell
                  userId={member._id}
                  idcardEncrypted={member.idcardEncrypted}
                  idcardUrl={member.idcardUrl}
                  userName={member.nama_lengkap}
                  fullWidth
                />
              </div>
            </div>

            {/* Rejection Reason (only for reject mode) */}
            {mode === "reject" && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Alasan Penolakan</p>
                <Textarea
                  placeholder="Contoh: Foto tidak jelas, wajah tidak terlihat, bukan foto terbaru..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="min-h-24"
                />
              </div>
            )}

            {/* Existing rejection reason (for approve mode) */}
            {mode === "approve" && member.fotoRejectionReason && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Alasan Penolakan Saat Ini</p>
                <p className="text-sm p-3 rounded-lg bg-destructive/10 text-destructive">
                  {member.fotoRejectionReason}
                </p>
              </div>
            )}
          </div>

          <SheetFooter className="px-6 pb-6">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Batal
            </Button>
            {mode === "reject" ? (
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={!reason.trim() || loading}
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Menyimpan...</>
                ) : (
                  <><XCircle className="mr-2 h-4 w-4" /> Tandai Ditolak</>
                )}
              </Button>
            ) : (
              <Button onClick={handleConfirm} disabled={loading}>
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Melepas...</>
                ) : (
                  <><CheckCircle2 className="mr-2 h-4 w-4" /> Lepas Hold</>
                )}
              </Button>
            )}
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ImageZoomModal
        open={zoomOpen}
        onOpenChange={setZoomOpen}
        src={zoomSrc}
        alt="Zoomed image"
        title={zoomTitle}
      />
    </>
  );
}
